import {Boundary} from "@floating-ui/dom";
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  Placement,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import {createContext, ReactNode, useContext, useMemo, useRef, useState} from "react";

import {TooltipContent} from "@/components/toolTip/subComponents/TooltipContent/TooltipContent.tsx";
import {TooltipTrigger} from "@/components/toolTip/subComponents/TooltipTrigger/TooltipTrigger.tsx";

export const TOOLTIP_VARIANTS = ["dark", "light", "chart"] as const;

export type TooltipVariant = (typeof TOOLTIP_VARIANTS)[number];

export interface TooltipOptions {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  boundary?: Boundary;
  tooltipContent?: ReactNode;
  delayMs?: number;
  onModal?: boolean;
  variant?: TooltipVariant;
  showArrow?: boolean;
  interactive?: boolean;
}

const ARROW_HEIGHT = 7;
const GAP = 2;

export function useTooltip({
  initialOpen = false,
  placement,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  boundary,
  delayMs,
  onModal,
  showArrow = true,
  interactive = false,
}: TooltipOptions = {}) {
  const arrowRef = useRef(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement: placement || undefined,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(showArrow ? ARROW_HEIGHT + GAP : GAP),
      flip({
        fallbackAxisSideDirection: "start",
        padding: 5,
        boundary: boundary ?? document.querySelector(`div.outlet-container > div.content-container`) ?? undefined,
      }),
      shift({
        padding: 5,
        boundary: boundary ?? document.querySelector(`div.outlet-container > div.content-container`) ?? undefined,
      }),
      ...(showArrow
        ? [
            arrow({
              element: arrowRef,
              padding: 7,
            }),
          ]
        : []),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
    delay: delayMs,
    handleClose: interactive ? safePolygon() : undefined,
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, {role: "tooltip"});

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      arrowRef,
      onModal,
      showArrow,
    }),
    [open, setOpen, interactions, data, arrowRef, onModal, showArrow]
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

export const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context == null) {
    throw new Error("Tooltip components must be wrapped in <Tooltip />");
  }

  return context;
};

// Stolen from https://floating-ui.com/docs/tooltip#reusable-tooltip-component with some minor modifications
const TooltipComponent = ({
  children,
  tooltipContent,
  variant = "dark",
  ...options
}: {children: ReactNode} & TooltipOptions) => {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);

  if (tooltipContent) {
    return (
      <TooltipContext.Provider value={tooltip}>
        <TooltipTrigger>{children}</TooltipTrigger>
        <TooltipContent variant={variant}>{tooltipContent}</TooltipContent>
      </TooltipContext.Provider>
    );
  }

  return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
};

export const Tooltip = Object.assign(TooltipComponent, {
  Content: TooltipContent,
  Trigger: TooltipTrigger,
});
